<template>
    <div class="sidebar-bottom-avatar">
        <div class="sidebar-bottom-avatar__leading">
            <div v-if="imageSrc && !imageErrored" class="sidebar-bottom-avatar__image">
                <img :src="imageSrc" alt="" @error="imageErrored = true" />
            </div>
            <div v-else-if="avatarMode === 'icon'" class="sidebar-bottom-avatar__icon-container">
                <slot name="icon" />
            </div>
            <div v-else class="sidebar-bottom-avatar__initials">
                <span>{{ initials }}</span>
            </div>
        </div>

        <div class="sidebar-bottom-avatar__text">
            <p class="sidebar-bottom-avatar__name">{{ name }}</p>
            <p v-if="supporting" class="sidebar-bottom-avatar__supporting">{{ supporting }}</p>
        </div>

        <div v-if="$slots.trailing" class="sidebar-bottom-avatar__trailing">
            <slot name="trailing" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { initials as nameInitials } from '@privacy-bee/format/text';

const props = withDefaults(
    defineProps<{
        name: string;
        supporting?: string;
        avatarMode?: 'initials' | 'icon';
        initialsOverride?: string;
        // A company logo or a person's avatar. The avatar host answers 404 for
        // anyone without a public one — a normal answer — so a failed load falls
        // back to initials, same as an absent src.
        imageSrc?: string | null;
    }>(),
    {
        avatarMode: 'initials',
    }
);

const imageErrored = ref(false);
// The row is reused across contexts as the sidebar switches, so the error flag
// follows the URL rather than the component instance.
watch(() => props.imageSrc, () => {
    imageErrored.value = false;
});

const initials = computed(() => {
    if (props.initialsOverride) return props.initialsOverride;
    return nameInitials(props.name);
});
</script>

<style lang="scss" scoped>
.sidebar-bottom-avatar {
    @apply flex items-center gap-3 w-full px-2 py-2 text-ink-invert;

    &__leading {
        @apply shrink-0;
    }

    &__initials {
        @apply w-10 h-10 rounded-full flex items-center justify-center border border-amber-500;
        background: rgba(245, 158, 11, 0.08);

        span {
            @apply text-amber-500 text-sm font-semibold leading-5;
        }
    }

    &__icon-container {
        @apply w-10 h-10 rounded flex items-center justify-center;
    }

    &__image {
        @apply w-10 h-10 rounded-full overflow-hidden bg-zinc-950 border border-zinc-700 flex items-center justify-center;

        img {
            @apply w-full h-full object-cover;
        }
    }

    &__text {
        @apply flex-1 min-w-0 flex flex-col items-start;
    }

    &__name {
        @apply text-sm font-semibold leading-5 text-ink-invert truncate w-full text-left;
    }

    &__supporting {
        @apply text-xs font-normal leading-[18px] text-zinc-400 truncate w-full text-left;
    }

    &__trailing {
        @apply shrink-0 flex items-center justify-center w-4 h-4 text-zinc-400;
    }
}
</style>
